import React, { useEffect, useState } from 'react';
import { App, Button, Typography, theme } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { API_URL } from '../../shared/api/apiUrl';
import { useAppColors } from '../../shared/theme';
import {
  MCP_DOCS_PATH,
  MCP_GUIDE_TEXT,
  MCP_TOOLS_JSON_PATH,
  MCP_URL_PLACEHOLDER,
  type McpSignInMode,
  claudeCodeCommand,
  claudeCodeTokenCommand,
  detectSignInMode,
  mcpServerUrl,
  sectionsFor,
} from './mcpGuideContent';

interface McpToolSummary {
  name: string;
  description: string;
}

type ToolsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; tools: McpToolSummary[] };

async function loadTools(signal: AbortSignal): Promise<McpToolSummary[]> {
  const response = await fetch(`${API_URL}${MCP_TOOLS_JSON_PATH}`, { signal });
  if (!response.ok) throw new Error(`tools.json: HTTP ${response.status}`);
  const body = (await response.json()) as { tools?: McpToolSummary[] };
  if (!Array.isArray(body.tools)) throw new Error('tools.json: нет поля tools');
  return body.tools;
}

/**
 * Гайд по MCP: адрес сервера, команда подключения, разделы и ЖИВОЙ список инструментов
 * с самого сервера — чтобы описание не разъезжалось с тем, что он реально отдаёт.
 */
const McpGuide: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const c = useAppColors();
  const [tools, setTools] = useState<ToolsState>({ status: 'loading' });
  const [mode, setMode] = useState<McpSignInMode | null>(null);
  const serverUrl = mcpServerUrl();
  const command = mode === 'browser' ? claudeCodeCommand(serverUrl) : claudeCodeTokenCommand(serverUrl);

  useEffect(() => {
    const controller = new AbortController();
    loadTools(controller.signal)
      .then((list) => setTools({ status: 'ready', tools: list }))
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        setTools({ status: 'error' });
      });
    detectSignInMode(serverUrl, controller.signal)
      .then(setMode)
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        // Сервер не ответил, есть ли у него вход через браузер. Токен в заголовке работает
        // на любом стенде, а совет «без токена» там, где входа нет, оставил бы без доступа.
        setMode('token');
      });
    return () => controller.abort();
  }, [serverUrl]);

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(command);
      message.success(MCP_GUIDE_TEXT.copied);
    } catch {
      message.error(MCP_GUIDE_TEXT.copy_failed);
    }
  };

  const codeBlockStyle: React.CSSProperties = {
    background: c.fillMuted,
    borderRadius: token.borderRadius,
    padding: token.paddingSM,
    fontFamily: token.fontFamilyCode,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
  };

  return (
    <div style={{ overflow: 'auto', maxHeight: 450, paddingRight: 8 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>{MCP_GUIDE_TEXT.title}</Typography.Title>

      <Typography.Text type="secondary">{MCP_GUIDE_TEXT.server_label}</Typography.Text>
      <pre style={codeBlockStyle}>{serverUrl}</pre>

      <Typography.Text type="secondary" style={{ display: 'block', marginTop: token.marginSM }}>
        {MCP_GUIDE_TEXT.command_label}
      </Typography.Text>
      {mode === null ? (
        <Typography.Paragraph type="secondary">{MCP_GUIDE_TEXT.mode_checking}</Typography.Paragraph>
      ) : (
        <>
          <pre style={codeBlockStyle}>{command}</pre>
          <div style={{ display: 'flex', gap: token.marginXS, alignItems: 'center', marginTop: token.marginXS, flexWrap: 'wrap' }}>
            <Button size="small" icon={<CopyOutlined />} onClick={copyCommand}>{MCP_GUIDE_TEXT.copy_button}</Button>
            <Button size="small" type="link" icon={<LinkOutlined />} href={`${API_URL}${MCP_DOCS_PATH}`} target="_blank" rel="noreferrer">
              {MCP_GUIDE_TEXT.docs_link}
            </Button>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {mode === 'browser' ? MCP_GUIDE_TEXT.command_hint : MCP_GUIDE_TEXT.command_hint_token}
            </Typography.Text>
          </div>
        </>
      )}

      {sectionsFor(mode).map((section) => (
        <div key={section.id} style={{ marginTop: token.marginMD }}>
          <Typography.Text strong>{section.title}</Typography.Text>
          {/* Текст — наш собственный модуль (не пользовательский ввод), как и гайд в i18n. */}
          <div
            className="documentation-guide-content"
            dangerouslySetInnerHTML={{ __html: section.html.split(MCP_URL_PLACEHOLDER).join(serverUrl) }}
          />
        </div>
      ))}

      <div style={{ marginTop: token.marginMD }}>
        <Typography.Text strong>{MCP_GUIDE_TEXT.tools_title}</Typography.Text>
        {tools.status === 'loading' && (
          <Typography.Paragraph type="secondary">{MCP_GUIDE_TEXT.tools_loading}</Typography.Paragraph>
        )}
        {tools.status === 'error' && (
          <Typography.Paragraph type="danger">{MCP_GUIDE_TEXT.tools_error}</Typography.Paragraph>
        )}
        {tools.status === 'ready' && (
          <>
            <Typography.Paragraph type="secondary">{MCP_GUIDE_TEXT.tools_count(tools.tools.length)}</Typography.Paragraph>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {tools.tools.map((tool) => (
                <li key={tool.name} style={{ marginBottom: 4 }}>
                  <code>{tool.name}</code> — {tool.description}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default McpGuide;
